import { useContext, useMemo } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Delete01Icon, Tick02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from "shared/components/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { MentionComponentAttributeNames } from "shared/components/Editor/core/extentions/mentions/types";
import type { SmsVariable } from "views/settings-communication/api/types";
import type { ContactUnitLinks } from "../api/crm";
import {
  ATTR_LINK_TYPE,
  ATTR_UNIT_ID,
  InboxVariableDataContext,
  linkForType,
  syncUnitScopedValueChips,
  unavailableLinkHint,
  type LinkType
} from "./link-variable-shared";

const labelForKey = (variables: SmsVariable[], key: string): string =>
  variables.find((v) => v.key === key)?.label ?? key;

// Name variable chip — display-only, unchanged behaviour.
const NameVariableChip = (props: NodeViewProps) => {
  const title = props.node.attrs[MentionComponentAttributeNames.Title] ?? "";
  const variableKey = props.node.attrs[MentionComponentAttributeNames.ID] ?? "";
  // Use the BE-provided label (same source the insert menu renders) so the
  // tooltip copy matches the menu, rather than a mechanically-derived label.
  // Read via context, NOT extension options — see InboxVariableDataContext.
  const { variables } = useContext(InboxVariableDataContext);
  const label = labelForKey(variables, variableKey);

  return (
    <NodeViewWrapper className="mention-component inline w-fit">
      <Tooltip placement="top">
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center rounded-sm bg-primary-20 text-primary-600 text-xs font-bold px-1.5 py-0.5 cursor-default"
            contentEditable={false}
          >
            {title}
          </span>
        </TooltipTrigger>
        <TooltipContent className="pointer-events-none whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs text-white z-[1000]">
          &lt;{label}&gt; variable
        </TooltipContent>
      </Tooltip>
    </NodeViewWrapper>
  );
};

// Link variable chip — unit submenu + resolution to a literal short URL.
const LinkVariableChip = (props: NodeViewProps) => {
  const { node, editor, deleteNode, updateAttributes } = props;
  const attrs = node.attrs as Record<string, unknown>;
  const linkType = (attrs[ATTR_LINK_TYPE] as LinkType | null) ?? null;
  const unitId = (attrs[ATTR_UNIT_ID] as number | null) ?? null;

  // Read via context, NOT extension options — see InboxVariableDataContext.
  const { variables, units } = useContext(InboxVariableDataContext);

  const variableKey =
    (attrs[MentionComponentAttributeNames.ID] as string) ?? linkType ?? "";
  const variableLabel = labelForKey(variables, variableKey);

  const selectedUnit = useMemo(
    () => units.find((u) => u.unit_id === unitId) ?? null,
    [units, unitId]
  );

  const selectedLink =
    selectedUnit && linkType ? linkForType(selectedUnit, linkType) : null;

  const isResolved = unitId !== null && !!selectedLink;
  const chipText = selectedUnit
    ? `${variableLabel} – ${selectedUnit.label}`
    : variableLabel;

  const chipClassName = isResolved
    ? "inline-flex items-center rounded-sm bg-primary-20 text-primary-600 text-xs font-bold px-1.5 py-0.5 cursor-pointer"
    : "inline-flex items-center rounded-sm bg-danger-50 text-danger-500 text-xs font-bold px-1.5 py-0.5 cursor-pointer ring-1 ring-danger-200";

  const handleSelectUnit = (unit: ContactUnitLinks) => {
    if (!linkType) return;
    const link = linkForType(unit, linkType);
    updateAttributes({
      [ATTR_UNIT_ID]: unit.unit_id,
      // title is what renderText returns -> the literal short URL in the
      // outgoing body. Empty when this unit has no link of this type.
      [MentionComponentAttributeNames.Title]: link?.short_url ?? ""
    });
    // Picking a unit disambiguates the lead, so any unit-scoped value chip
    // elsewhere in the doc can now show this unit's real value.
    syncUnitScopedValueChips(editor, unit);
  };

  if (!editor.isEditable)
    return (
      <NodeViewWrapper className="mention-component inline w-fit">
        <span className={chipClassName} contentEditable={false}>
          {chipText}
        </span>
      </NodeViewWrapper>
    );

  return (
    <NodeViewWrapper className="mention-component inline w-fit">
      <Popover placement="bottom-start">
        <PopoverTrigger asChild>
          <button
            type="button"
            className={chipClassName}
            contentEditable={false}
          >
            {chipText}
          </button>
        </PopoverTrigger>
        <PopoverContent className="z-50 w-72 max-h-[320px] overflow-y-auto rounded-lg bg-white shadow-xl ring-1 ring-alpha-100 overflow-clip">
          <button
            type="button"
            onClick={() => deleteNode()}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-base font-bold text-primary-500 hover:bg-alpha-40"
          >
            <HugeiconsIcon icon={Delete01Icon} size={20} />
            Remove
          </button>

          {units.length === 0 ? (
            <div className="px-4 py-3 text-sm font-medium text-content-300 border-t border-alpha-40">
              No units available for this contact
            </div>
          ) : (
            units.map((unit) => {
              const link = linkType ? linkForType(unit, linkType) : null;
              const isCurrent = unit.unit_id === unitId;
              return (
                <button
                  key={unit.unit_id}
                  type="button"
                  disabled={!link}
                  onClick={() => handleSelectUnit(unit)}
                  className="flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left border-t border-alpha-40 hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="flex w-full items-center justify-between gap-2">
                    <span className="text-sm font-medium text-content-1000">
                      {unit.label}
                    </span>
                    {isCurrent ? (
                      <HugeiconsIcon
                        icon={Tick02Icon}
                        size={18}
                        className="text-content-1000 shrink-0"
                      />
                    ) : null}
                  </span>
                  <span className="text-xs font-medium text-content-300 break-all">
                    {link
                      ? link.display_url
                      : unavailableLinkHint(unit, linkType)}
                  </span>
                </button>
              );
            })
          )}
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
};

// Single node view that branches on whether the chip is a link variable.
export const InboxVariableChip = (props: NodeViewProps) => {
  const linkType =
    (props.node.attrs as Record<string, unknown>)[ATTR_LINK_TYPE] ?? null;
  return linkType ? (
    <LinkVariableChip {...props} />
  ) : (
    <NameVariableChip {...props} />
  );
};
